/*
 * Alorae currency formatting — ES module
 * Replacement for legacy `theme.Currency` in assets/theme.js.
 * See docs/alorae-rebuild/decompose-theme-files-roadmap.md §Phase 2 Round 2.
 */

const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/;

/**
 * Internal helper: formats a number (in cents) into a string using the
 * given precision and thousands/decimal separators.
 *
 * @param {number|string} number - amount in cents
 * @param {number} precision - decimal places (default 2)
 * @param {string} thousands - thousands separator (default ',')
 * @param {string} decimal - decimal separator (default '.')
 * @returns {string}
 */
function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
  if (number == null || isNaN(number)) {
    return '0';
  }

  const fixed = (Number(number) / 100.0).toFixed(precision);
  const parts = fixed.split('.');
  const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
  const centsAmount = parts[1] ? `${decimal}${parts[1]}` : '';

  return dollarsAmount + centsAmount;
}

/**
 * Formats a cents integer (e.g. 1995) into a localized money string
 * (e.g. "$19.95") using a Shopify money format code.
 *
 * Supported placeholders:
 *   {{amount}}                                      — 1,234.56
 *   {{amount_no_decimals}}                          — 1,235
 *   {{amount_with_comma_separator}}                 — 1.234,56
 *   {{amount_no_decimals_with_comma_separator}}     — 1.235
 *   {{amount_with_apostrophe_separator}}            — 1'234.56
 *   {{amount_no_decimals_with_space_separator}}     — 1 235
 *   {{amount_with_space_separator}}                 — 1 234,56
 *   {{amount_with_period_and_space_separator}}      — 1 234.56
 *
 * @param {number|string} cents - amount in cents
 * @param {string} [format='${{amount}}'] - Shopify money format string
 * @returns {string}
 */
export function formatMoney(cents, format = '${{amount}}') {
  let amount = cents;

  if (typeof amount === 'string') {
    amount = amount.replace('.', '');
  }

  const formatString = format || '${{amount}}';
  const match = formatString.match(PLACEHOLDER_REGEX);

  if (!match) {
    return formatString;
  }

  let value = '';

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(amount, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(amount, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(amount, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(amount, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(amount, 2, "'", '.');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(amount, 0, ' ');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(amount, 2, ' ', ',');
      break;
    case 'amount_with_period_and_space_separator':
      value = formatWithDelimiters(amount, 2, ' ', '.');
      break;
    default:
      value = formatWithDelimiters(amount, 2);
  }

  return formatString.replace(PLACEHOLDER_REGEX, value);
}

/**
 * Formats a cents integer using `Intl.NumberFormat`, honoring the shop's
 * active currency. Modern replacement for `formatMoney`.
 *
 * Resolution order for currency code:
 *   1. `window.Shopify.currency.active`
 *   2. `currencyCode` argument
 *   3. `'USD'`
 *
 * @param {number|string} cents - amount in cents
 * @param {string|null} [currencyCode=null] - ISO 4217 currency fallback
 * @returns {string}
 */
export function formatCurrency(cents, currencyCode = null) {
  const numericCents = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents);
  const amount = isNaN(numericCents) ? 0 : numericCents / 100;

  let code = null;
  if (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
    code = window.Shopify.currency.active;
  }
  if (!code) {
    code = currencyCode || 'USD';
  }

  const locale = (typeof window !== 'undefined' && window.Shopify && window.Shopify.locale) || undefined;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code
    }).format(amount);
  } catch (e) {
    // Invalid currency code — fall back to USD.
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  }
}

/**
 * Converts an amount in cents between two currencies using a simple
 * cross-rate. Returns the converted amount in cents (rounded).
 *
 * If `fromCurrency === toCurrency`, the input is returned unchanged.
 *
 * @param {number} cents - source amount in cents
 * @param {string} fromCurrency - source ISO 4217 code
 * @param {string} toCurrency - target ISO 4217 code
 * @param {number} rate - multiplier: 1 unit `fromCurrency` = `rate` units `toCurrency`
 * @returns {number} converted amount in cents (integer)
 */
export function convert(cents, fromCurrency, toCurrency, rate) {
  const numericCents = Number(cents);
  if (isNaN(numericCents)) {
    return 0;
  }

  if (fromCurrency === toCurrency) {
    return Math.round(numericCents);
  }

  const numericRate = Number(rate);
  if (isNaN(numericRate) || numericRate <= 0) {
    return Math.round(numericCents);
  }

  return Math.round(numericCents * numericRate);
}
